import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Device and browser detection utilities for push notification support
 */

/** Check if running in browser environment (not SSR) */
const isBrowser = typeof window !== "undefined" && typeof navigator !== "undefined";

export function isIOS(): boolean {
  if (!isBrowser) return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

export function isSafari(): boolean {
  if (!isBrowser) return false;
  const ua = navigator.userAgent;
  // Safari: has Safari in UA but NOT Chrome/CriOS/Firefox/FxiOS
  return /Safari/.test(ua) && !/Chrome|CriOS|Firefox|FxiOS|Edg/.test(ua);
}

export function isStandalonePWA(): boolean {
  if (!isBrowser) return false;
  // Check if running as standalone PWA (added to home screen)
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export type IOSBrowserInfo = {
  isIOS: boolean;
  isSafari: boolean;
  isChrome: boolean;
  isFirefox: boolean;
  isStandalone: boolean;
  canReceivePush: boolean;
};

// Cache browser info - but only cache after PWA detection is stable
// We don't cache on iOS until we're sure about standalone mode
let cachedBrowserInfo: IOSBrowserInfo | null = null;

export function getIOSBrowserInfo(forceRefresh = false): IOSBrowserInfo {
  // Return cached value unless force refresh is requested
  if (cachedBrowserInfo && !forceRefresh) return cachedBrowserInfo;

  if (!isBrowser) {
    return {
      isIOS: false,
      isSafari: false,
      isChrome: false,
      isFirefox: false,
      isStandalone: false,
      canReceivePush: true,
    };
  }

  const ua = navigator.userAgent;
  const ios = isIOS();
  const safari = isSafari();
  const isChrome = /CriOS/.test(ua);
  const isFirefox = /FxiOS/.test(ua);
  const standalone = isStandalonePWA();

  const info: IOSBrowserInfo = {
    isIOS: ios,
    isSafari: safari,
    isChrome,
    isFirefox,
    isStandalone: standalone,
    // iOS push notifications only work in Safari when added to Home Screen
    canReceivePush: !ios || (safari && standalone),
  };

  // Only cache if we're confident about the standalone detection
  // On iOS Safari standalone, navigator.standalone should be true immediately
  // On non-iOS, we can cache right away
  if (!ios || standalone) {
    cachedBrowserInfo = info;
  }

  return info;
}

/**
 * Clear the cached browser info - useful for testing or when PWA state changes
 */
export function clearBrowserInfoCache(): void {
  cachedBrowserInfo = null;
}
